/**
 * Público: lo que la pantalla dice de cada persona, en palabras. Puro y testeado (`publico.test.ts`).
 */

import { num, plural } from "../../campana-nueva/utils/format";
import type { Actividad, CanalFiltro, PersonaPublico } from "../types";

export const R_PUBLICO = "/activa/publico";
export const rutaFicha = (clave: string) => `${R_PUBLICO}/${encodeURIComponent(clave)}`;

export const TEXTO_ACTIVIDAD: Record<Actividad, string> = {
  respondio: "Respondió (cualquier canal)",
  respondio_wa: "Respondió por WhatsApp",
  respondio_mail: "Contestó el mail",
  abrio_mail: "Abrió el mail",
  clic_mail: "Hizo clic en el mail",
  contactado: "Ya le escribimos",
  sin_contactar: "Sin contactar",
  baja: "Pidió la baja",
};

export const TEXTO_CANAL: Record<CanalFiltro, string> = {
  whatsapp: "Tiene WhatsApp",
  mail: "Tiene mail",
  ambos: "Tiene los dos",
  solo_whatsapp: "Sólo WhatsApp",
  solo_mail: "Sólo mail",
};

export type Tono = "verde" | "violeta" | "gris" | "rojo" | "ambar";

/** El estado de WhatsApp de una persona, en una frase y con su tono. */
export function estadoWhatsApp(p: Pick<PersonaPublico, "telefonos" | "wa" | "baja">): { texto: string; tono: Tono; via: string } {
  const via = p.wa.porApi && p.wa.porQr ? "API y QR" : p.wa.porApi ? "API" : p.wa.porQr ? "QR" : "";
  if (p.baja.whatsapp) return { texto: "Pidió la baja", tono: "rojo", via };
  if (p.wa.respuestas > 0) return { texto: p.wa.respuestas === 1 ? "Respondió" : `Respondió ${num(p.wa.respuestas)} veces`, tono: "verde", via };
  if (p.wa.leidos > 0) return { texto: "Leyó", tono: "violeta", via };
  if (p.wa.entregados > 0) return { texto: "Le llegó", tono: "violeta", via };
  if (p.wa.enviados > 0) return { texto: `${num(p.wa.enviados)} ${plural(p.wa.enviados, "enviado", "enviados")}`, tono: "gris", via };
  if (!p.telefonos.length) return { texto: "Sin celular", tono: "gris", via };
  return { texto: "Sin contactar", tono: "gris", via };
}

/**
 * Los estados en los que todavía no pasó nada: la tabla los muestra apagados, sin chip. Un chip gris «Sin contactar» en
 * casi todas las filas no decía nada y tapaba lo que sí pasó (auditoría de diseño del 18/10/2026).
 */
export const SIN_NOVEDAD: ReadonlySet<string> = new Set(["Sin contactar", "Sin celular", "Sin mail"]);

/** La segunda línea de la fila: el celular y el mail, sin repetir el que ya hace de nombre. */
export function contactoDeLaFila(p: Pick<PersonaPublico, "nombre" | "emails" | "telefonos">): string {
  const nombre = nombreVisible(p);
  return [p.telefonos[0], p.emails[0]].filter((x) => x && x !== nombre).join(" · ") || "—";
}

/** El estado del mail de una persona, en una frase y con su tono. */
export function estadoMail(p: Pick<PersonaPublico, "emails" | "mail" | "baja">): { texto: string; tono: Tono } {
  if (p.baja.mail) return { texto: "Pidió la baja", tono: "rojo" };
  if (p.mail.respuestas > 0) return { texto: "Contestó", tono: "verde" };
  if (p.mail.clics > 0) return { texto: "Hizo clic", tono: "verde" };
  if (p.mail.aperturas > 0) return { texto: p.mail.aperturas === 1 ? "Abrió" : `Abrió ${num(p.mail.aperturas)} veces`, tono: "violeta" };
  if (p.mail.rebotes > 0 && p.mail.enviados <= p.mail.rebotes) return { texto: "Rebotó", tono: "ambar" };
  if (p.mail.enviados > 0) return { texto: `${num(p.mail.enviados)} ${plural(p.mail.enviados, "enviado", "enviados")}`, tono: "gris" };
  if (!p.emails.length) return { texto: "Sin mail", tono: "gris" };
  return { texto: "Sin contactar", tono: "gris" };
}

/** «hace 3 días», «hoy», «hace 2 meses»: para la última interacción y la última compra. */
export function haceCuanto(iso: string | null | undefined, ahora: Date = new Date()): string {
  if (!iso) return "—";
  const t = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).getTime();
  if (isNaN(t)) return "—";
  const dias = Math.floor((ahora.getTime() - t) / 86_400_000);
  if (dias <= 0) return "hoy";
  if (dias === 1) return "ayer";
  if (dias < 31) return `hace ${dias} días`;
  // los cumplidos, no redondeados: del 1/3 al 12/10 son «7 meses», no «8»
  const meses = Math.floor(dias / 30.44);
  if (meses < 12) return `hace ${meses} ${plural(meses, "mes", "meses")}`;
  const anios = Math.floor(dias / 365.25);
  return `hace ${anios} ${plural(anios, "año", "años")}`;
}

/** Las iniciales para el círculo de la ficha. */
export function iniciales(nombre: string, respaldo = ""): string {
  const partes = String(nombre || "").trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return (respaldo.trim()[0] || "?").toUpperCase();
  return ((partes[0][0] || "") + (partes.length > 1 ? partes[partes.length - 1][0] : "")).toUpperCase();
}

/** Cómo se llama a la persona cuando no tiene nombre: su mail, o su celular enmascarado. */
export const nombreVisible = (p: Pick<PersonaPublico, "nombre" | "emails" | "telefonos">) =>
  p.nombre || p.emails[0] || p.telefonos[0] || "Sin nombre";

/** Hace cuánto que no armamos el público, en palabras (para el aviso de arriba). */
export function textoArmado(armadoAt: string | null, enCurso: boolean, ahora: Date = new Date()): string {
  if (enCurso) return "Armando el público…";
  if (!armadoAt) return "Todavía no se armó el público.";
  const min = Math.max(0, Math.round((ahora.getTime() - new Date(armadoAt).getTime()) / 60_000));
  if (min < 1) return "Actualizado recién.";
  if (min < 60) return `Actualizado hace ${min} ${plural(min, "minuto", "minutos")}.`;
  const h = Math.round(min / 60);
  return `Actualizado hace ${h} ${plural(h, "hora", "horas")}.`;
}
